import { supabase } from '../supabase.js';

/**
 * NotificationService - Detects and manages proactive notifications
 * Uses real data sources to generate actionable alerts
 */
class NotificationService {
  constructor() {
    this.publicHolidayCache = new Map();
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
  }

  /**
   * Fetch public holidays for a country using Nager.Date API (free, no auth required)
   * @param {string} countryCode - ISO 3166-1 alpha-2 country code (e.g., 'CN', 'AU', 'VN')
   * @param {number} year - Year to fetch holidays for
   */
  async getPublicHolidays(countryCode, year = new Date().getFullYear()) {
    const cacheKey = `${countryCode}-${year}`;
    
    // Check cache first
    if (this.publicHolidayCache.has(cacheKey)) {
      const cached = this.publicHolidayCache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheExpiry) {
        return cached.data;
      }
    }

    try {
      const response = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`);
      if (!response.ok) {
        console.warn(`Could not fetch holidays for ${countryCode}:`, response.statusText);
        return [];
      }
      
      const holidays = await response.json();
      
      // Cache the result
      this.publicHolidayCache.set(cacheKey, {
        data: holidays,
        timestamp: Date.now()
      });
      
      return holidays;
    } catch (error) {
      console.error('Error fetching public holidays:', error);
      return [];
    }
  }

  /**
   * Check for upcoming public holidays that might affect a shipment
   */
  async checkShipmentHolidayImpact(consignment) {
    const notifications = [];
    
    if (!consignment.DestinationCountry || !consignment.ETD_Planned) {
      return notifications;
    }

    // Map country names to ISO codes
    const countryCodeMap = {
      'China': 'CN',
      'Vietnam': 'VN',
      'Australia': 'AU',
      'New Zealand': 'NZ',
      'USA': 'US',
      'United States': 'US',
      'Italy': 'IT',
      'Germany': 'DE',
      'United Kingdom': 'GB',
      'Japan': 'JP',
      'South Korea': 'KR',
      'India': 'IN'
    };

    const countryCode = countryCodeMap[consignment.DestinationCountry];
    if (!countryCode) {
      return notifications;
    }

    const etd = new Date(consignment.ETD_Planned);
    const holidays = await this.getPublicHolidays(countryCode, etd.getFullYear());
    
    // Check for holidays within 7 days of ETD
    const checkWindow = 7 * 24 * 60 * 60 * 1000; // 7 days
    
    for (const holiday of holidays) {
      const holidayDate = new Date(holiday.date);
      const timeDiff = holidayDate - etd;
      
      if (timeDiff > 0 && timeDiff < checkWindow) {
        const daysUntil = Math.ceil(timeDiff / (24 * 60 * 60 * 1000));
        
        notifications.push({
          notification_type: 'public_holiday',
          severity: 'warning',
          title: `Public Holiday Alert: ${consignment.DestinationCountry}`,
          message: `${holiday.name} on ${holiday.date} may cause delays. Shipment ETD is ${daysUntil} day${daysUntil !== 1 ? 's' : ''} before holiday.`,
          entity_type: 'consignment',
          entity_id: consignment.ConsignmentID,
          related_data: {
            holiday_name: holiday.name,
            holiday_date: holiday.date,
            country: consignment.DestinationCountry,
            etd: consignment.ETD_Planned,
            days_until_holiday: daysUntil
          },
          event_date: holidayDate,
          expires_at: new Date(holidayDate.getTime() + 7 * 24 * 60 * 60 * 1000) // Expire 7 days after holiday
        });
      }
    }

    return notifications;
  }

  /**
   * Check for upcoming farm audits
   */
  async checkUpcomingAudits(farmPIC) {
    const notifications = [];
    
    try {
      // Fetch audit records for this farm
      const { data: audits, error } = await supabase
        .from('audit_records')
        .select('*')
        .eq('PIC', farmPIC)
        .order('AuditDate', { ascending: false });

      if (error || !audits || audits.length === 0) {
        return notifications;
      }

      const now = new Date();
      
      // Check for audits due soon (based on last audit + typical cycle)
      const lastAudit = audits[0];
      const lastAuditDate = new Date(lastAudit.AuditDate);
      const auditCycleDays = 365; // Annual audits
      const nextAuditDue = new Date(lastAuditDate.getTime() + auditCycleDays * 24 * 60 * 60 * 1000);
      const daysUntilDue = Math.ceil((nextAuditDue - now) / (24 * 60 * 60 * 1000));

      // Alert if audit is due within 30 days
      if (daysUntilDue > 0 && daysUntilDue <= 30) {
        // Check for open findings from last audit
        const openFindings = lastAudit.Findings ? JSON.parse(lastAudit.Findings).filter(f => f.status !== 'resolved') : [];
        
        let message = `Farm audit due in ${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''}.`;
        if (openFindings.length > 0) {
          message += ` ${openFindings.length} item${openFindings.length !== 1 ? 's' : ''} from previous audit need${openFindings.length === 1 ? 's' : ''} resolution.`;
        }

        notifications.push({
          notification_type: 'audit_due',
          severity: daysUntilDue <= 7 ? 'critical' : 'warning',
          title: 'Farm Audit Due Soon',
          message,
          entity_type: 'farm',
          entity_id: farmPIC,
          related_data: {
            last_audit_date: lastAudit.AuditDate,
            next_audit_due: nextAuditDue.toISOString(),
            days_until_due: daysUntilDue,
            open_findings: openFindings,
            audit_type: lastAudit.AuditType || 'NWD-IP'
          },
          event_date: nextAuditDue,
          expires_at: nextAuditDue
        });
      }
    } catch (error) {
      console.error('Error checking audits:', error);
    }

    return notifications;
  }

  /**
   * Check for animal health/fit-to-transport issues
   */
  async checkAnimalHealthIssues(mobId) {
    const notifications = [];
    
    try {
      // Check LPA welfare records for fitness issues
      const { data: welfareRecords, error } = await supabase
        .from('lpa_welfare')
        .select('*')
        .eq('MobID', mobId)
        .order('RecordDate', { ascending: false })
        .limit(10);

      if (error || !welfareRecords || welfareRecords.length === 0) {
        return notifications;
      }

      // Check recent records for failed fitness tests
      const recentFailures = welfareRecords.filter(record => {
        const recordDate = new Date(record.RecordDate);
        const daysSince = (new Date() - recordDate) / (24 * 60 * 60 * 1000);
        return daysSince <= 7 && record.FitToTransport === false;
      });

      if (recentFailures.length > 0) {
        const failureCount = recentFailures.reduce((sum, r) => sum + (r.AnimalCount || 1), 0);
        
        // Count consignments - fetch and filter since OriginPICs is a JSON array
        const { data: consignments, error: consignmentError } = await supabase
          .from('export_documents')
          .select('ConsignmentID, OriginPICs');
        
        let consignmentCount = 0;
        if (!consignmentError && consignments) {
          consignmentCount = consignments.filter(c => {
            if (!c.OriginPICs) return false;
            try {
              const picsArray = Array.isArray(c.OriginPICs) ? c.OriginPICs : JSON.parse(c.OriginPICs || '[]');
              return picsArray.includes(mobId);
            } catch (e) {
              return false;
            }
          }).length;
        }
        
        notifications.push({
          notification_type: 'health_check',
          severity: 'critical',
          title: 'Fit-to-Transport Failure',
          message: `${failureCount} animal${failureCount !== 1 ? 's' : ''} from this mob failed fit-to-transport assessment in the last 7 days.`,
          entity_type: 'mob',
          entity_id: mobId,
          related_data: {
            failure_count: failureCount,
            recent_failures: recentFailures.map(r => ({
              date: r.RecordDate,
              count: r.AnimalCount,
              reason: r.FailureReason || 'Not specified'
            })),
            consignment_count: consignmentCount
          },
          event_date: new Date(recentFailures[0].RecordDate),
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
        });
      }
    } catch (error) {
      console.error('Error checking animal health:', error);
    }

    return notifications;
  }

  /**
   * Check for expiring certifications
   */
  async checkCertificationExpiry(farmPIC) {
    const notifications = [];
    
    try {
      const { data: certs, error } = await supabase
        .from('sustainability_certificates')
        .select('*')
        .eq('PIC', farmPIC);

      if (error || !certs || certs.length === 0) {
        return notifications;
      }

      const now = new Date();
      
      for (const cert of certs) {
        if (!cert.ExpiryDate) continue;
        
        const expiryDate = new Date(cert.ExpiryDate);
        const daysUntilExpiry = Math.ceil((expiryDate - now) / (24 * 60 * 60 * 1000));

        // Alert if expiring within 60 days
        if (daysUntilExpiry > 0 && daysUntilExpiry <= 60) {
          notifications.push({
            notification_type: 'certification_expiry',
            severity: daysUntilExpiry <= 14 ? 'critical' : 'warning',
            title: `${cert.Scheme} Certification Expiring`,
            message: `${cert.Scheme} certification expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''} (${cert.ExpiryDate}).`,
            entity_type: 'farm',
            entity_id: farmPIC,
            related_data: {
              scheme: cert.Scheme,
              cert_number: cert.CertNumber,
              expiry_date: cert.ExpiryDate,
              days_until_expiry: daysUntilExpiry
            },
            event_date: expiryDate,
            expires_at: expiryDate
          });
        }
      }
    } catch (error) {
      console.error('Error checking certifications:', error);
    }

    return notifications;
  }

  /**
   * Get all active notifications for an entity
   */
  async getNotificationsForEntity(entityType, entityId) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .eq('is_active', true)
        .eq('is_dismissed', false)
        .gte('expires_at', new Date().toISOString())
        .order('severity', { ascending: true }) // critical first
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getNotificationsForEntity:', error);
      return [];
    }
  }

  /**
   * Generate notifications for an entity (farm, consignment, etc.)
   */
  async generateNotifications(entityType, entityData) {
    const allNotifications = [];

    try {
      switch (entityType) {
        case 'farm':
          const auditNotifs = await this.checkUpcomingAudits(entityData.PIC);
          const certNotifs = await this.checkCertificationExpiry(entityData.PIC);
          allNotifications.push(...auditNotifs, ...certNotifs);
          break;

        case 'consignment':
        case 'export':
          const holidayNotifs = await this.checkShipmentHolidayImpact(entityData);
          allNotifications.push(...holidayNotifs);
          break;

        case 'mob':
          const healthNotifs = await this.checkAnimalHealthIssues(entityData.MobID);
          allNotifications.push(...healthNotifs);
          break;
      }

      // Save notifications to database
      if (allNotifications.length > 0) {
        const { error } = await supabase
          .from('notifications')
          .upsert(allNotifications, {
            onConflict: 'entity_type,entity_id,notification_type',
            ignoreDuplicates: false
          });

        if (error) {
          console.error('Error saving notifications:', error);
        }
      }

      return allNotifications;
    } catch (error) {
      console.error('Error generating notifications:', error);
      return [];
    }
  }

  /**
   * Dismiss a notification
   */
  async dismissNotification(notificationId, userId = 'system') {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          is_dismissed: true,
          dismissed_at: new Date().toISOString(),
          dismissed_by: userId
        })
        .eq('id', notificationId);

      if (error) {
        console.error('Error dismissing notification:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in dismissNotification:', error);
      return false;
    }
  }
}

export default new NotificationService();
